import { DEEP_LINK_SCHEME_PREFIX } from './deepLinkScheme';

/**
 * Public "open" links used in emails.
 *
 * Every email CTA points at this https namespace on the admin domain. The
 * hosting rewrite serves a static landing page there that opens the app
 * (custom scheme) when installed and offers the store buttons otherwise.
 * Phase 3 of docs/plans/universal-links.md lets the app claim `/open/*`
 * directly; the URL shape does not change when that lands.
 */
export const OPEN_LINK_BASE_URL = 'https://event.flatrockglobal.com/open';

/**
 * "Download" links are for readers who do not have the app yet (the
 * new-user invitation). The app never claims this namespace, so the phone
 * always reaches the landing page, which forwards it to the reader's store.
 * On a computer the page shows a QR code instead.
 */
export const DOWNLOAD_LINK_BASE_URL = 'https://event.flatrockglobal.com/download';

export function buildEventDownloadLink(eventId: string): string {
  return `${DOWNLOAD_LINK_BASE_URL}/events/${encodeURIComponent(eventId)}`;
}

export function buildEventOpenLink(eventId: string): string {
  return `${OPEN_LINK_BASE_URL}/events/${encodeURIComponent(eventId)}`;
}

export function buildSessionOpenLink(eventId: string, sessionId: string): string {
  return `${buildEventOpenLink(eventId)}/sessions/${encodeURIComponent(sessionId)}`;
}

export function buildSurveyOpenLink(eventId: string, surveyId: string): string {
  return `${buildEventOpenLink(eventId)}/surveys/${encodeURIComponent(surveyId)}`;
}

export function buildInboxOpenLink(): string {
  return `${OPEN_LINK_BASE_URL}/inbox`;
}

/**
 * Converts an in-app deep link (`com.serenica.eventplanner://events/...`)
 * to its https open-link twin for use in email. Push payloads keep the
 * custom scheme because they are consumed inside the app. Unrecognised
 * input passes through unchanged so a bad value never becomes a worse one.
 */
export function toOpenLink(deepLink: string): string {
  if (deepLink.startsWith(OPEN_LINK_BASE_URL)) return deepLink;
  if (!deepLink.startsWith(DEEP_LINK_SCHEME_PREFIX)) return deepLink;
  const path = deepLink.slice(DEEP_LINK_SCHEME_PREFIX.length).replace(/^\/+/, '');
  return path ? `${OPEN_LINK_BASE_URL}/${path}` : OPEN_LINK_BASE_URL;
}
